// What the header shows about the OpenAI spend of an admin: the text of the two amounts, how a trading-api answer or a
// failed request changes what is shown, and the view of it. Import-free CommonJS, so it can be tested in plain node --
// see src/lib/ai-optimization.js. Nothing here talks to the server or owns a timer.
//
// The figures are what OpenAI's Costs API reports and can lag by a few hours: an unknown number is a dash, and a number
// that could not be refreshed stays on screen but greyed. An error, from the server or from the request, is a small
// yellow exclamation icon right after the label with the reason on its own hover, while the hover on the amounts only
// says how old they are. The user asked for that icon at UAT 05.1 (gap G-05.1-1). That replaces, for the icon only, D-17
// ("No warning colour or threshold. The colour stays neutral always.") and this file's earlier rule from plan 05.1-05
// ("Never a warning colour or an alert."). Still never an alert or a toast.

// trading-api caches the figures for 5 minutes (CACHE_TTL_MS in trading-api src/lib/openai-spend.js), measured from the
// start of its last read. Polling exactly every 5 minutes let a poll that arrived a few ms early get the cached answer,
// so 'as of' moved only every 5 or 10 minutes (WR-61, UAT 05.1 gap G-05.1-1). 5 min 30 s lands every poll on an expired
// cache. Keep SPEND_POLL_MS at least 30 s above SERVER_CACHE_MS (D-14 amended: about every 5 1/2 minutes).
const SERVER_CACHE_MS = 300000;
const SPEND_POLL_MS = 330000;

const SPEND_LABEL = 'OpenAI';
const DASH = '—';
const NO_FIGURES_REASON = 'OpenAI costs are not available right now';
const NO_ANSWER_REASON = 'the server did not answer';
const NO_FIGURES_TOOLTIP = 'no figures yet';
const LAG_NOTE = ' (OpenAI figures can lag by a few hours)';

const INITIAL_SPEND_STATE = { figures: null, reason: null };

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

// '$1.50' for usd or an unknown currency, '2.00 EUR' for another 3-letter code, a dash for anything that is not a number
const formatSpend = (value, currency) => {
	if (!isFiniteNumber(value)) {
		return DASH;
	}
	const amount = value.toFixed(2);
	if (typeof currency === 'string' && /^[a-z]{3}$/i.test(currency) && currency.toLowerCase() !== 'usd') {
		return `${amount} ${currency.toUpperCase()}`;
	}
	return `$${amount}`;
};

// The new state after an outcome of getOpenAiSpend (apiAction: { ok, data } or { ok: false, error }). Figures are only
// replaced by an answer that has a month number; otherwise the last good figures stay and the reason says why
const nextSpendState = (previous, outcome) => {
	if (outcome?.ok) {
		const data = outcome.data || {};
		const error = typeof data.error === 'string' && data.error ? data.error : null;
		if (isFiniteNumber(data.month)) {
			return {
				figures: {
					today: isFiniteNumber(data.today) ? data.today : null,
					month: data.month,
					currency: typeof data.currency === 'string' ? data.currency : null,
					asOf: typeof data.asOf === 'string' ? data.asOf : null,
				},
				reason: error,
			};
		}
		return { figures: previous.figures, reason: error || NO_FIGURES_REASON };
	}
	const reason = (typeof outcome?.error === 'string' && outcome.error) || NO_ANSWER_REASON;
	return { figures: previous.figures, reason };
};

// { label, text, stale, tooltip, error } of a state; formatTime turns the asOf string into the clock text of the tooltip.
// The tooltip belongs to the amounts and only tells their age; the reason, if any, is the error
const spendView = (state, formatTime) => {
	const { figures, reason } = state;
	const error = reason || null;
	if (!figures) {
		return { label: SPEND_LABEL, text: DASH, stale: true, tooltip: reason ? NO_FIGURES_TOOLTIP : 'loading', error };
	}
	const today = formatSpend(figures.today, figures.currency);
	const month = formatSpend(figures.month, figures.currency);
	const text = `${today} today · ${month} month`;
	const asOf = figures.asOf ? `as of ${formatTime(figures.asOf)}` : 'as of an unknown time';
	return { label: SPEND_LABEL, text, stale: Boolean(reason), tooltip: `${asOf}${LAG_NOTE}`, error };
};

module.exports = {
	SPEND_POLL_MS,
	SERVER_CACHE_MS,
	SPEND_LABEL,
	INITIAL_SPEND_STATE,
	formatSpend,
	nextSpendState,
	spendView,
};
